import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Card, Spinner } from './shared';
import SyncConfigFormModal, { EMPTY_SYNC_CONFIG } from './SyncConfigFormModal';
import SyncRunModal from './SyncRunModal';

const STATUS_VARIANT = { success: 'success', partial: 'warning', error: 'danger' };
const RESOURCE_TYPE_LABELS = { skill: 'Skill', subagent: 'Sub-agent', knowledge: 'Knowledge' };

export default function AdminSyncConfigs() {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [runningId, setRunningId] = useState(null);
    const [filterResourceType, setFilterResourceType] = useState('');
    const [filterProvider, setFilterProvider] = useState('');

    useEffect(() => { load(); }, [filterResourceType, filterProvider]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { includeInactive: true };
            if (filterResourceType) params.resourceType = filterResourceType;
            if (filterProvider) params.provider = filterProvider;
            const res = await adminApi.listSyncConfigs(params);
            setConfigs(Array.isArray(res) ? res : []);
            setError(null);
        } catch (e) {
            setError(e.message || 'Failed to load configs');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => { setEditing({ ...EMPTY_SYNC_CONFIG }); setFormOpen(true); };
    const openEdit = (cfg) => { setEditing({ ...cfg }); setFormOpen(true); };

    const handleSave = async (payload) => {
        const isExisting = configs.some((c) => c.id === payload.id);
        if (isExisting) await adminApi.updateSyncConfig(payload.id, payload);
        else await adminApi.createSyncConfig(payload);
        setFormOpen(false);
        setSuccess(`Saved config "${payload.id}"`);
        await load();
    };

    const handleDelete = async (id) => {
        if (!confirm(`Delete sync config "${id}"?`)) return;
        try {
            await adminApi.deleteSyncConfig(id);
            setSuccess(`Deleted "${id}"`);
            await load();
        } catch (e) {
            setError(e.message || 'Failed to delete');
        }
    };

    const runningConfig = configs.find((c) => c.id === runningId);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Sync Configs</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Provider-agnostic sync rules — pair a credential with a resource type.
                    </p>
                </div>
                <Button onClick={openCreate}>+ New config</Button>
            </div>

            <div className="flex items-center gap-2">
                <select
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filterResourceType}
                    onChange={(e) => setFilterResourceType(e.target.value)}
                >
                    <option value="">All resource types</option>
                    {Object.entries(RESOURCE_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select
                    className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={filterProvider}
                    onChange={(e) => setFilterProvider(e.target.value)}
                >
                    <option value="">All providers</option>
                    <option value="git">git</option>
                </select>
            </div>

            {error && <Alert variant="danger" onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}

            {loading ? (
                <Spinner center text="Loading…" />
            ) : configs.length === 0 ? (
                <Card><div className="text-center text-sm text-gray-500 py-8">No sync configs yet</div></Card>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Config</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Type</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Provider / Credential</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Branch / Paths</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Env / Tags</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Last Sync</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-56">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {configs.map((c) => (
                                <tr key={c.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-medium text-gray-900">{c.name}</span>
                                            <Badge variant="outline">{c.id}</Badge>
                                            {!c.is_active && <Badge variant="warning">inactive</Badge>}
                                        </div>
                                        {c.description && <p className="text-xs text-gray-400 mt-0.5">{c.description}</p>}
                                    </td>
                                    <td className="px-3 py-3"><Badge variant="info">{RESOURCE_TYPE_LABELS[c.resource_type] || c.resource_type}</Badge></td>
                                    <td className="px-3 py-3">
                                        <div className="text-xs text-gray-700">
                                            <Badge variant="default">{c.provider}</Badge>
                                            <code className="ml-1 bg-gray-100 px-1 rounded">{c.credential_id}</code>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        {c.branch && <Badge variant="outline" className="mb-1">@ {c.branch}</Badge>}
                                        <div className="flex flex-col gap-0.5 text-xs text-gray-600 font-mono">
                                            {(c.paths || []).length === 0
                                                ? <span className="text-gray-400">/ (root)</span>
                                                : (c.paths || []).map((p, i) => <span key={i}>{p}</span>)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3">
                                        <Badge variant="default" dot>{c.target_environment}</Badge>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {(c.tags || []).map((t) => <Badge key={t} variant="info" className="text-xs">#{t}</Badge>)}
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-xs text-gray-500">
                                        {c.last_synced_at ? (
                                            <>
                                                <div>{new Date(c.last_synced_at).toLocaleString()}</div>
                                                <Badge variant={STATUS_VARIANT[c.last_status] || 'default'}>{c.last_status || '-'}</Badge>
                                            </>
                                        ) : '—'}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="xs" onClick={() => setRunningId(c.id)}>Sync now</Button>
                                            <Button variant="ghost" size="xs" onClick={() => openEdit(c)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <SyncConfigFormModal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                initial={editing}
                isExisting={editing && configs.some((c) => c.id === editing.id)}
                onSave={handleSave}
            />

            <SyncRunModal
                open={!!runningId}
                configId={runningId}
                resourceType={runningConfig?.resource_type}
                onClose={() => { setRunningId(null); load(); }}
                onSuccess={(msg) => setSuccess(msg)}
            />
        </div>
    );
}
